import { useCallback, useEffect, useRef, useState, type TouchEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { heroSlides } from '@/mocks/movies';
import { getItemById, typeLabel } from '@/utils/media';

export default function HeroCarousel() {
  const { t } = useTranslation();
  const slides = heroSlides.map((id) => getItemById(id)).filter(Boolean);
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);

  const go = useCallback(
    (next: number) => {
      setIndex((prev) => {
        const total = slides.length;
        return (next + total) % total;
      });
    },
    [slides.length],
  );

  useEffect(() => {
    const timer = setInterval(() => setIndex((prev) => (prev + 1) % slides.length), 6000);
    return () => clearInterval(timer);
  }, [slides.length]);

  if (slides.length === 0) return null;

  const onTouchStart = (e: TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const onTouchEnd = (e: TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      go(index + (delta < 0 ? 1 : -1));
    }
    touchStartX.current = null;
  };

  return (
    <section
      className="relative h-[440px] w-full overflow-hidden md:h-[600px]"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {slides.map((item, i) =>
        item ? (
          <div
            key={item.id}
            className={`absolute inset-0 transition-opacity duration-700 ${i === index ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
          >
            <div className="absolute inset-0 w-full h-full">
              <img
                src={item.banner}
                alt={`${item.title}剧照`}
                title={`${item.title} ${typeLabel(item.type)} 精品推荐`}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/60 to-primary-950/20"></div>
          </div>
        ) : null,
      )}

      <div className="relative z-10 mx-auto flex h-full max-w-page flex-col justify-end px-4 pb-12 md:px-6 md:pb-16">
        <span className="mb-3 inline-flex w-fit items-center gap-2 rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-accent-950">
          <i className="ri-fire-fill"></i>
          {t('heroTag')}
        </span>
        <h1 className="max-w-2xl font-heading text-3xl font-extrabold leading-tight text-background-50 md:text-5xl">
          {slides[index]?.title}
        </h1>
        <p className="mt-3 max-w-xl text-sm text-background-100/85 md:text-base">{slides[index]?.tagline}</p>
        <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-background-100/80 md:text-sm">
          <span className="flex items-center gap-1 rounded-full bg-background-50/10 px-3 py-1 backdrop-blur">
            <i className="ri-star-fill text-accent-400"></i>
            {slides[index]?.rating.toFixed(1)}
          </span>
          <span className="rounded-full bg-background-50/10 px-3 py-1 backdrop-blur">{typeLabel(slides[index]!.type)}</span>
          <span className="rounded-full bg-background-50/10 px-3 py-1 backdrop-blur">{slides[index]?.year}</span>
          <span className="rounded-full bg-background-50/10 px-3 py-1 backdrop-blur">{slides[index]?.genres.join(' · ')}</span>
        </div>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link
            to={`/detail/${slides[index]?.id}`}
            className="inline-flex items-center gap-2 rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-accent-950 transition hover:bg-accent-400 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-play-circle-line text-lg"></i>
            {t('heroPlayInfo')}
          </Link>
          <Link
            to={`/category/${slides[index]?.type}`}
            className="inline-flex items-center gap-2 rounded-md border border-background-50/40 px-5 py-2.5 text-sm font-semibold text-background-50 transition hover:bg-background-50/10 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-apps-2-line text-lg"></i>
            {typeLabel(slides[index]!.type)}{t('navCategories')}
          </Link>
        </div>

        <div className="mt-8 flex items-center gap-3">
          <button
            type="button"
            onClick={() => go(index - 1)}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-background-50/30 text-background-50 transition hover:bg-background-50/15 cursor-pointer"
            aria-label="上一张"
          >
            <i className="ri-arrow-left-s-line text-xl"></i>
          </button>
          <button
            type="button"
            onClick={() => go(index + 1)}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-background-50/30 text-background-50 transition hover:bg-background-50/15 cursor-pointer"
            aria-label="下一张"
          >
            <i className="ri-arrow-right-s-line text-xl"></i>
          </button>
          <div className="ml-2 flex items-center gap-2">
            {slides.map((item, i) => (
              <button
                key={item?.id ?? i}
                type="button"
                onClick={() => go(i)}
                aria-label={`切换到第${i + 1}张`}
                className={`h-2 rounded-full transition-all cursor-pointer ${i === index ? 'w-7 bg-accent-500' : 'w-2 bg-background-50/40'}`}
              ></button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}